<template>
  <div>
    <validation-observer ref="validator" #default="{ valid }">
      <b-form
        :class="[inline ? 'gap-2 items-center' : '']"
        :inline="inline"
        @submit.prevent="addDestination"
        @reset.prevent="clearForm"
      >
        <validation-provider
          #default="validationContext"
          :name="$t('reporting_channel.workflow_config.destinations.name')"
          rules="required"
          class="flex-grow"
          slim
        >
          <b-form-group
            id="input-group-1"
            :label="$t('reporting_channel.workflow_config.destinations.name')"
            label-for="destination_name"
          >
            <b-form-input
              id="destination_name"
              v-model="destination.name"
              :placeholder="
                $t('reporting_channel.workflow_config.destinations.name')
              "
              class="required"
            >
            </b-form-input>
            <b-form-invalid-feedback :class="[{ absolute: inline }]">
              {{ validationContext.errors[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>
        <!-- E-mail-->
        <validation-provider
          #default="validationContext"
          :name="$t('reporting_channel.workflow_config.destinations.email')"
          rules="required|email"
          class="flex-grow"
          slim
        >
          <b-form-group
            id="input-group-2"
            :label="$t('reporting_channel.workflow_config.destinations.email')"
            label-for="destination_email"
          >
            <b-form-input
              id="destination_email"
              type="email"
              v-model="destination.email"
              :placeholder="
                $t('reporting_channel.workflow_config.destinations.email')
              "
              class="required"
            >
            </b-form-input>
            <b-form-invalid-feedback :class="[{ absolute: inline }]">
              {{ validationContext.errors[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>
        <!-- Status de Tratamento-->
        <b-form-group
          v-if="destination"
          id="input-group-3"
          :label="$t('reporting_channel.deadline_config.handling_status')"
          label-for="destination_handling_status"
          class="flex-grow"
        >
          <v-select
            v-model="destination.handling_status"
            :options="handlingStatusOptions"
            :reduce="option => option.key"
            :get-option-label="option => (option ? option.label : '')"
            placeholder="Selecione o(s) status (opcional)"
            :clearable="true"
            multiple
            :close-on-select="false"
          >
            <template slot="no-options">
              <span>Nenhuma situação encontrada</span>
            </template>
            <template slot="selected-option-container" slot-scope="{ option }">
              <span
                :class="
                  isHexColor(option.color)
                    ? 'badge badge-glow'
                    : `badge badge-glow badge-${option.color || 'secondary'}`
                "
                :style="
                  isHexColor(option.color)
                    ? `background-color: ${option.color}; margin-right: 4px; margin-bottom: 2px; display: inline-block;`
                    : 'margin-right: 4px; margin-bottom: 2px; display: inline-block;'
                "
              >
                {{ option.label }}
              </span>
            </template>
            <template slot="option" slot-scope="option">
              <span
                :class="
                  isHexColor(option.color)
                    ? 'badge badge-glow'
                    : `badge badge-glow badge-${option.color || 'secondary'}`
                "
                :style="
                  isHexColor(option.color)
                    ? `background-color: ${option.color}; margin-right: 8px;`
                    : 'margin-right: 8px;'
                "
              >
                {{ option.label }}
              </span>
            </template>
          </v-select>
          <small class="text-danger" v-if="errors && errors.handling_status">{{
            errors.handling_status[0]
          }}</small>
        </b-form-group>

        <div class="flex flex-row justify-end gap-4">
          <b-button
            v-permission="
              `reporting_channel_handling.configuration.destinations.create`
            "
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            class="px-1"
            variant="outline-primary"
            @click="addDestination"
            :disabled="!valid"
          >
            <feather-icon icon="PlusIcon" class="mr-50" />
            <span class="align-middle">{{ $t('add') }}</span>
          </b-button>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            class="px-1"
            variant="outline-primary"
            @click="$bvModal.show('search-contact')"
          >
            <b-icon-search class="mr-50" />
            <span class="align-middle">{{
              $t('action.search_contact') || 'Buscar Contato'
            }}</span>
          </b-button>
        </div>
      </b-form>
    </validation-observer>

    <div class="vs-row w-full pt-2" v-if="service && id">
      <Grid
        ref="grid-destination"
        :hide_actions="false"
        :service="service"
        :route_grid_path="`/${id}/destinations/grid`"
        route_name="handling_status_configuration_index"
        order_column="name"
        direction="asc"
        :column_formats="columnFormats"
      >
        <template slot="actions" slot-scope="actions">
          <feather-icon
            v-permission="
              `reporting_channel_handling.configuration.destinations.delete`
            "
            v-tooltip="'Remover destinatário'"
            icon="Trash2Icon"
            class="h-5 w-5 mb-1 mr-1 stroke-current text-danger"
            @click="confirmDeleteDestination(actions.row)"
          />
        </template>
      </Grid>

      <div class="flex flex-row justify-end gap-4 mt-3">
        <b-button @click="handleBack" variant="outline-secondary">
          {{ $t('action.back') }}
        </b-button>
      </div>
    </div>
    <!-- Pop contact-->
    <calendar-contact-popup @selected="addContactList" />
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, email } from '@validations'
import Ripple from 'vue-ripple-directive'
import {
  BForm,
  BFormGroup,
  BFormInput,
  BButton,
  BFormInvalidFeedback,
  BIconSearch
} from 'bootstrap-vue'
import Grid from '@/components/Grid.vue'
import ReportingChannelWorkflowDestinationService from '@/services/api/reporting-channel/ReportingChannelWorkflowDestinationService'
import CalendarContactPopup from '@/views/apps/calendar/calendar-event-handler/CalendarContactPopup.vue'
import GenericService from '@/services/api/GenericService'
import vSelect from 'vue-select'

export default {
  name: 'DestinatariosTab',
  directives: {
    Ripple
  },
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    ValidationObserver,
    ValidationProvider,
    BFormInvalidFeedback,
    BIconSearch,
    Grid,
    CalendarContactPopup,
    vSelect
  },
  props: {
    id: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      destination: {
        name: '',
        email: '',
        handling_status: null
      },
      service: null,
      genericService: null,
      required,
      email,
      inline: false,
      handlingStatusOptions: []
    }
  },
  computed: {
    columnFormats() {
      return {
        created_at: val => {
          return this.$utils.format.date(val)
        },
        handling_status_label: (val, model) => {
          if (
            !model ||
            !model.handling_status_array ||
            !Array.isArray(model.handling_status_array) ||
            model.handling_status_array.length === 0
          ) {
            return '<span class="badge badge-glow badge-secondary">Todos</span>'
          }
          return model.handling_status_array
            .map(status => {
              const color = status && status.color ? status.color : 'secondary'
              const label = status && status.label ? status.label : ''
              const isHex = color && color.startsWith('#')
              if (isHex) {
                return (
                  '<span class="badge badge-glow" style="background-color: ' +
                  color +
                  '; margin-right: 4px;">' +
                  label +
                  '</span>'
                )
              }
              return (
                '<span class="badge badge-glow badge-' +
                color +
                '" style="margin-right: 4px;">' +
                label +
                '</span>'
              )
            })
            .join('')
        }
      }
    }
  },
  beforeMount() {
    // Garantir que destination está inicializado
    if (!this.destination) {
      this.destination = {
        name: '',
        email: '',
        handling_status: null
      }
    }
    this.service = ReportingChannelWorkflowDestinationService.build(this.$vs)
    this.genericService = GenericService.build(this.$vs)
    this.updateGrid()
    this.loadHandlingStatus()
  },
  methods: {
    updateGrid() {
      const grid = this.$refs['grid-destination']
      if (grid) {
        grid.fetchGridData()
      }
    },
    handleBack() {
      this.$router.push({ name: 'handling_status_configuration_index' })
    },
    loadHandlingStatus() {
      if (!this.genericService) {
        this.genericService = GenericService.build(this.$vs)
      }

      if (this.genericService && this.genericService.getFormHandlingStatus) {
        this.genericService.getFormHandlingStatus().then(
          data => {
            this.handlingStatusOptions = data || []
          },
          error => {
            console.error('Erro ao carregar status de tratamento:', error)
            this.handlingStatusOptions = [
              { key: 'OP', label: 'Aberto', color: '#5E50EE' },
              { key: 'IV', label: 'Em validação', color: 'info' },
              { key: 'IH', label: 'Em tratamento', color: 'warning' },
              { key: 'PE', label: 'Pendente', color: 'danger' },
              { key: 'VA', label: 'Validado', color: 'success' },
              { key: 'CL', label: 'Fechado', color: 'dark' }
            ]
          }
        )
      } else {
        // Fallback se o serviço não estiver disponível
        this.handlingStatusOptions = [
          { key: 'OP', label: 'Aberto', color: '#5E50EE' },
          { key: 'IV', label: 'Em validação', color: 'info' },
          { key: 'IH', label: 'Em tratamento', color: 'warning' },
          { key: 'PE', label: 'Pendente', color: 'danger' },
          { key: 'VA', label: 'Validado', color: 'success' },
          { key: 'CL', label: 'Fechado', color: 'dark' }
        ]
      }
    },
    addContactList(contactList = null) {
      if (!this.id) {
        this.notifyWarning(
          this.$vs,
          'É necessário salvar a configuração antes de adicionar destinatários'
        )
        return
      }

      if (
        contactList &&
        this._.isArray(contactList) &&
        contactList.length > 0
      ) {
        let destinations = []

        contactList.forEach(contact =>
          destinations.push({
            name: contact.name,
            email: contact.email,
            handling_status:
              this.destination.handling_status &&
              this.destination.handling_status.length > 0
                ? this.destination.handling_status
                : []
          })
        )

        this.addWorkflowDestination(destinations)
      }
    },
    addDestination() {
      if (!this.id) {
        this.notifyWarning(
          this.$vs,
          'É necessário salvar a configuração antes de adicionar destinatários'
        )
        return
      }

      const destinations = [this.destination]
      this.addWorkflowDestination(destinations)
    },
    addWorkflowDestination(destinations) {
      this.$vs.loading()
      if (this.id) {
        this.service
          .addDestination(this.id, destinations)
          .then(
            response => {
              this.notifySuccess(this.$vs, this.$t('messages.success'))
              this.clearForm()
            },
            error => {
              this.notifyError(
                this.$vs,
                'Não foi possível adicionar o(s) destinatário(s)!'
              )
            }
          )
          .finally(() => {
            this.$vs.loading.close()
            this.updateGrid()
          })
      }
    },
    clearForm() {
      this.destination.name = ''
      this.destination.email = ''
      this.destination.handling_status = []
    },
    confirmDeleteDestination(item) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        acceptText: this.$t('action.yes'),
        cancelText: this.$t('action.no'),
        title: this.$t('action.remove'),
        text: this.$t('messages.confirm_deletion') + ` ${item.name}`,
        accept: () => {
          this.deleteDestination(item)
        }
      })
    },
    deleteDestination(entity) {
      this.$vs.loading()
      this.service
        .deleteDestination(this.id, entity.id)
        .then(
          response => {
            this.notifySuccess(this.$vs, this.$t('messages.success'))
            this.updateGrid()
          },
          error => {
            this.notifyWarning(this.$vs, this.$t('messages.error'))
          }
        )
        .finally(() => {
          this.$vs.loading.close()
        })
    },
    getFormattedValue(row, key) {
      const grid = this.$refs['grid-destination']
      if (grid && grid.column_formats && grid.column_formats[key]) {
        const formatter = grid.column_formats[key]
        return formatter(row[key], row)
      }
      return row[key] || ''
    },
    getStatusColor(statusKey) {
      const status = this.handlingStatusOptions.find(s => s.key === statusKey)
      return status ? status.color || 'secondary' : 'secondary'
    },
    isHexColor(color) {
      return color && typeof color === 'string' && color.startsWith('#')
    }
  }
}
</script>

<style lang="scss" scoped></style>
